import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Users, AlertTriangle, Sparkles } from "lucide-react";

function money(n, currency = "usd") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `$${Math.round(n)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function BillingSummaryWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    api
      .get("/billing/summary")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  // Only show the widget for users on a paid plan
  if (!data.plan_id) return null;

  const openPortal = async () => {
    setOpening(true);
    const t = toast.loading("Opening Stripe Customer Portal…");
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not open billing portal",
        { id: t }
      );
      setOpening(false);
    }
  };

  const renewal = fmtDate(data.next_renewal);

  return (
    <div
      data-testid="billing-summary-widget"
      className="mt-6 border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          <CreditCard className="h-3 w-3" />
          Plan &amp; seats
        </div>
        <div className="flex items-center gap-3">
          {data.is_trial && (
            <span
              data-testid="billing-summary-trial-badge"
              className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700"
            >
              <Sparkles className="h-3 w-3" /> Trial
            </span>
          )}
          {data.is_owner && data.has_customer && (
            <Button
              size="sm"
              variant="outline"
              data-testid="billing-summary-manage-btn"
              disabled={opening}
              onClick={openPortal}
              className="rounded-sm border-slate-300"
            >
              Manage subscription
            </Button>
          )}
        </div>
      </div>
      <div className="grid gap-px bg-slate-100 sm:grid-cols-4">
        <div className="bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Plan
          </div>
          <div
            data-testid="billing-summary-plan"
            className="kpi-stat mt-2 font-heading text-2xl font-semibold capitalize"
          >
            {data.plan_name || data.plan_id}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {money(data.plan_price, data.currency)} /mo base
          </div>
        </div>

        <div className="bg-white p-4">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            <Users className="h-3 w-3" /> Seats
          </div>
          <div
            data-testid="billing-summary-seats"
            className="kpi-stat mt-2 font-heading text-2xl font-semibold tabular-nums"
          >
            {data.team_size}
            <span className="text-slate-400"> / {data.included_seats}</span>
            {data.extra_seats > 0 && (
              <span
                data-testid="billing-summary-extra-seats"
                className="ml-2 text-xs font-medium text-amber-700"
              >
                +{data.extra_seats} extra
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {data.extra_seats > 0
              ? `${data.extra_seats} × ${money(data.extra_seat_price, data.currency)} /seat`
              : "Within plan limit"}
          </div>
        </div>

        <div className="bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Monthly total
          </div>
          <div
            data-testid="billing-summary-total"
            className="kpi-stat mt-2 font-heading text-2xl font-semibold tabular-nums text-blue-700"
          >
            {money(data.total_monthly, data.currency)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {data.extra_seats > 0
              ? `${money(data.plan_price, data.currency)} + ${money(
                  data.extra_seats * data.extra_seat_price,
                  data.currency
                )}`
              : "Base plan only"}
          </div>
        </div>

        <div className="bg-white p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            {data.is_trial ? "Trial ends" : "Next renewal"}
          </div>
          <div
            data-testid="billing-summary-renewal"
            className="kpi-stat mt-2 font-heading text-lg font-semibold"
          >
            {renewal || "—"}
          </div>
          <div className="mt-1">
            {data.billing_problem ? (
              <Link
                to="/billing"
                data-testid="billing-summary-problem-link"
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"
              >
                <AlertTriangle className="h-3 w-3" /> Payment issue — fix now
              </Link>
            ) : (
              <Link
                to="/team"
                className="text-xs font-medium text-blue-700 hover:underline"
              >
                Manage team →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
